import { Component, OnDestroy, OnInit } from '@angular/core';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons/faEllipsisH';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, skip, Subscription, take } from 'rxjs';
import { HelperService } from '../../../services/helper/helper.service';
import { JobResultInterface } from '../../../models/interface/jobresult.interface';
import { GetAllJobResultsOfUserPayload, QueueActionTypes } from '../../../store/queue/queue.action';

@Component({
  selector: 'app-dialog-downloads',
  templateUrl: './dialog-downloads.component.html',
  styleUrls: ['./dialog-downloads.component.scss'],
})
export class DialogDownloadsComponent implements OnInit, OnDestroy {
  // Icons
  faRedo = faRedo;
  faTimes = faTimes;
  faEllipsisH = faEllipsisH;
  faFilePdf = faFilePdf;
  faClock = faClock;

  public isLoading$: Observable<boolean>;

  public submitted = false;

  public pagedJobAnswersheets$: Observable<JobResultInterface[]>;
  public pagedJobInstances$: Observable<JobResultInterface[]>;
  public pagedJobStudies$: Observable<JobResultInterface[]>;

  public jobresultsSubject = new BehaviorSubject<JobResultInterface[]>([]);
  public jobAnswersheetsSubject = new BehaviorSubject<JobResultInterface[]>([]);
  public jobInstancesSubject = new BehaviorSubject<JobResultInterface[]>([]);
  public jobStudiesSubject = new BehaviorSubject<JobResultInterface[]>([]);

  public jobAnswersheets$: Observable<JobResultInterface[]>;
  public jobInstances$: Observable<JobResultInterface[]>;
  public jobStudies$: Observable<JobResultInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private allJobResults$: Observable<JobResultInterface[]>;

  private pagedJobAnswersheetsSubject = new BehaviorSubject<JobResultInterface[]>([]);
  private pagedJobInstancesSubject = new BehaviorSubject<JobResultInterface[]>([]);
  private pagedJobStudiesSubject = new BehaviorSubject<JobResultInterface[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<{ allJobResults: JobResultInterface[] }>,
    private helperService: HelperService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.allJobResults$ = store.select('allJobResults');
    this.jobAnswersheets$ = this.jobAnswersheetsSubject.asObservable();
    this.jobInstances$ = this.jobInstancesSubject.asObservable();
    this.jobStudies$ = this.jobStudiesSubject.asObservable();
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.pagedJobAnswersheets$ = this.pagedJobAnswersheetsSubject.asObservable();
    this.pagedJobInstances$ = this.pagedJobInstancesSubject.asObservable();
    this.pagedJobStudies$ = this.pagedJobStudiesSubject.asObservable();
    this.loadJobResults();
  }

  public loadJobResults(): void {
    const payload: GetAllJobResultsOfUserPayload = {
      jobType: 'Answersheet,Intervention_Instance,Study',
    };
    this.store.dispatch({
      type: QueueActionTypes.getAllJobResultsOfUserType,
      payload,
    });
    this.allJobResults$.pipe(skip(1), take(1)).subscribe((result: JobResultInterface[]) => {
      const sortedJobs = result.sort(this.helper.sortByIdDesc);
      this.jobAnswersheetsSubject.next(
        sortedJobs.filter((job) => job.attributes.type === 'Answersheet'),
      );
      this.jobInstancesSubject.next(
        sortedJobs.filter((job) => job.attributes.type === 'Intervention_Instance'),
      );
      this.jobStudiesSubject.next(
        sortedJobs.filter((job) => job.attributes.type === 'Study'),
      );
      this.jobresultsSubject.next(sortedJobs);
      this.pagedJobAnswersheetsSubject.next(this.jobAnswersheetsSubject.value.slice(0, 20));
      this.pagedJobInstancesSubject.next(this.jobInstancesSubject.value.slice(0, 20));
      this.pagedJobStudiesSubject.next(this.jobStudiesSubject.value.slice(0, 20));
      this.isLoadingSubject.next(false);
    });
  }

  public updatePagedJobAnswersheets(event: JobResultInterface[]) {
    if (event) {
      this.pagedJobAnswersheetsSubject.next(event);
    }
  }

  public updatePagedJobInstances(event: JobResultInterface[]) {
    if (event) {
      this.pagedJobInstancesSubject.next(event);
    }
  }

  public updatePagedJobStudies(event: JobResultInterface[]) {
    if (event) {
      this.pagedJobStudiesSubject.next(event);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
